import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, ChevronLeft, ChevronRight, AlertTriangle, Clock, User, MapPin, Edit, Trash2, UserCircle, FileEdit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Calendar as CalendarPicker } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { cn } from '../../lib/utils';

const initialFormData = {
  client_id: '', worker_id: '', job_type: 'mantenimiento', title: '', description: '',
  scheduled_date: '', scheduled_time: '', recurrence: 'once', is_emergency: false, estimated_duration: 60,
  template_id: ''  // Plantilla a usar para el parte
};

const CalendarPage = () => {
  const { api, user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailOpen, setJobDetailOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  // Create combined list of assignees (owner + workers)
  const allAssignees = [
    // Add owner as first option with special ID
    { id: `owner_${user?.id || 'self'}`, name: `${user?.name || 'Yo'} (Propietario)`, isOwner: true, actualId: user?.id },
    // Add all workers
    ...workers.map(w => ({ ...w, isOwner: false, actualId: w.id }))
  ];

  useEffect(() => { 
    fetchData(); 
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, clientsRes, workersRes, templatesRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/clients'),
        api.get('/workers'),
        api.get('/report-templates')
      ]);
      setJobs(jobsRes.data);
      setClients(clientsRes.data);
      setWorkers(workersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getJobsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return jobs.filter(job => job.scheduled_date === dateStr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingJob) {
        const updateData = {
          client_id: formData.client_id,
          worker_id: formData.worker_id || null,
          job_type: formData.job_type,
          title: formData.title,
          description: formData.description,
          scheduled_date: formData.scheduled_date,
          scheduled_time: formData.scheduled_time,
          is_emergency: formData.is_emergency,
          estimated_duration: formData.estimated_duration,
          template_id: formData.template_id || null
        };
        await api.put('/jobs/' + editingJob.id, updateData);
        toast.success('Trabajo actualizado correctamente');
      } else {
        await api.post('/jobs', {
          ...formData,
          scheduled_date: formData.scheduled_date || format(selectedDate || new Date(), 'yyyy-MM-dd'),
          template_id: formData.template_id || null
        });
        toast.success(formData.is_emergency ? '¡Trabajo de urgencia creado!' : 'Trabajo programado correctamente');
      }
      closeDialog();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar el trabajo'));
    } finally {
      setSubmitting(false);
    }
  };

  const openJobDetail = (job) => {
    console.log('Opening job detail:', job);
    setSelectedJob(job);
    setJobDetailOpen(true);
  };

  const handleEdit = (job) => {
    setJobDetailOpen(false);
    setSelectedJob(null);
    setEditingJob(job);
    setFormData({
      client_id: job.client_id,
      worker_id: job.worker_id || '',
      job_type: job.job_type,
      title: job.title,
      description: job.description || '',
      scheduled_date: job.scheduled_date,
      scheduled_time: job.scheduled_time || '',
      recurrence: job.recurrence || 'once',
      is_emergency: job.is_emergency,
      estimated_duration: job.estimated_duration || 60
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (job) => {
    setJobDetailOpen(false);
    setSelectedJob(null);
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async (deleteSeries) => {
    if (!jobToDelete) return;
    try {
      let url = '/jobs/' + jobToDelete.id;
      if (deleteSeries) {
        url = url + '?delete_series=true';
      }
      await api.delete(url);
      toast.success(deleteSeries ? 'Serie de trabajos eliminada' : 'Trabajo eliminado');
      setDeleteDialogOpen(false);
      setJobToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el trabajo');
    }
  };

  const openNewJob = (date) => {
    setEditingJob(null);
    setSelectedDate(date);
    setFormData({ ...initialFormData, scheduled_date: format(date, 'yyyy-MM-dd') });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
    setFormData(initialFormData);
  };

  const getStatusColor = (status) => {
    const colors = { scheduled: 'bg-blue-500', in_progress: 'bg-yellow-500', completed: 'bg-green-500', cancelled: 'bg-gray-400' };
    return colors[status] || colors.scheduled;
  };

  const getStatusLabel = (status) => {
    const labels = { scheduled: 'Programado', in_progress: 'En progreso', completed: 'Completado', cancelled: 'Cancelado' };
    return labels[status] || status;
  };

  const isRecurring = (job) => job.recurrence && job.recurrence !== 'once';

  if (loading) {
    return <OwnerLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div></div></OwnerLayout>;
  }

  const dialogTitle = editingJob ? 'Editar trabajo' : 'Programar trabajo';
  const submitBtnText = submitting ? 'Guardando...' : (editingJob ? 'Guardar cambios' : 'Programar');

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="calendar-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(addDays(currentDate, -7))}><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="font-display text-xl font-semibold capitalize">{format(currentDate, "MMMM yyyy", { locale: es })}</h2>
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(addDays(currentDate, 7))}><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>Hoy</Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-job-btn"><Plus className="w-4 h-4 mr-2" />Nuevo trabajo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{dialogTitle}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })} required>
                    <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name} - {c.address}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Asignar a</Label>
                  <Select value={formData.worker_id || "unassigned"} onValueChange={(v) => setFormData({ ...formData, worker_id: v === "unassigned" ? "" : v })}>
                    <SelectTrigger data-testid="job-worker-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Sin asignar</SelectItem>
                      {allAssignees.map(a => (
                        <SelectItem key={a.id} value={a.actualId}>
                          <div className="flex items-center gap-2">
                            {a.isOwner ? <UserCircle className="w-4 h-4 text-sky-600" /> : <User className="w-4 h-4" />}
                            {a.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                        <SelectItem value="reparacion">Reparación</SelectItem>
                        <SelectItem value="instalacion">Instalación</SelectItem>
                        <SelectItem value="revision">Revisión</SelectItem>
                        <SelectItem value="urgencia">Urgencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {!editingJob && (
                    <div className="space-y-2">
                      <Label>Recurrencia</Label>
                      <Select value={formData.recurrence} onValueChange={(v) => setFormData({ ...formData, recurrence: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="once">Una vez</SelectItem>
                          <SelectItem value="weekly">Semanal</SelectItem>
                          <SelectItem value="biweekly">Quincenal</SelectItem>
                          <SelectItem value="twice_weekly">2 veces/semana</SelectItem>
                          <SelectItem value="monthly">Mensual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="job-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.scheduled_date && "text-muted-foreground")}>
                          {formData.scheduled_date ? format(parseISO(formData.scheduled_date), "PPP", { locale: es }) : "Seleccionar"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><CalendarPicker mode="single" selected={formData.scheduled_date ? parseISO(formData.scheduled_date) : undefined} onSelect={(date) => date && setFormData({ ...formData, scheduled_date: format(date, 'yyyy-MM-dd') })} /></PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Hora</Label>
                    <Input type="time" value={formData.scheduled_time} onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
                </div>
                
                {/* Template selector */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileEdit className="w-4 h-4" />
                    Plantilla para el parte
                  </Label>
                  <Select 
                    value={formData.template_id || "default"} 
                    onValueChange={(v) => setFormData({ ...formData, template_id: v === "default" ? "" : v })}
                  >
                    <SelectTrigger data-testid="job-template-select">
                      <SelectValue placeholder="Plantilla por defecto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Plantilla por defecto</SelectItem>
                      {templates.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} {t.is_default && '(predeterminada)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">La plantilla se usará cuando el trabajador complete el parte</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /><span className="font-medium text-red-900">Marcar como urgencia</span></div>
                  <Switch checked={formData.is_emergency} onCheckedChange={(v) => setFormData({ ...formData, is_emergency: v })} />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog}>Cancelar</Button>
                  <Button type="submit" disabled={submitting} data-testid="save-job-btn">{submitBtnText}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Week View Header */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map(day => (
            <div key={day.toISOString()} className="text-center">
              <p className="text-xs text-slate-500 uppercase">{format(day, 'EEE', { locale: es })}</p>
              <p className={cn("text-lg font-semibold", isSameDay(day, new Date()) && "text-sky-600")}>{format(day, 'd')}</p>
            </div>
          ))}
        </div>

        {/* Week View Grid */}
        <div className="grid grid-cols-7 gap-2 min-h-[400px]">
          {weekDays.map(day => {
            const dayJobs = getJobsForDay(day);
            const isToday = isSameDay(day, new Date());
            return (
              <Card key={day.toISOString()} className={cn("min-h-[150px]", isToday && "ring-2 ring-sky-500")}>
                <CardContent className="p-2 space-y-1">
                  {dayJobs.length === 0 ? (
                    <div 
                      onClick={() => openNewJob(day)} 
                      className="w-full h-full min-h-[100px] flex items-center justify-center text-slate-300 hover:text-slate-400 hover:bg-slate-50 rounded transition-colors cursor-pointer"
                    >
                      <Plus className="w-6 h-6" />
                    </div>
                  ) : (
                    <>
                      {dayJobs.slice(0, 3).map(job => (
                        <div
                          key={job.id}
                          onClick={() => openJobDetail(job)}
                          className={cn(
                            "w-full p-2 rounded text-xs text-left cursor-pointer transition-all border-2 border-transparent hover:border-sky-400",
                            job.is_emergency ? "bg-red-100 hover:bg-red-150" : "bg-slate-50 hover:bg-slate-100"
                          )}
                          role="button"
                          tabIndex={0}
                          data-testid={`job-card-${job.id}`}
                        >
                          <div className="flex items-center gap-1 mb-1">
                            <div className={cn("w-2 h-2 rounded-full flex-shrink-0", getStatusColor(job.status))} />
                            <span className="font-medium truncate">{job.title}</span>
                          </div>
                          {job.scheduled_time && <p className="text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />{job.scheduled_time}</p>}
                          <p className="text-slate-500 truncate">{job.client_name}</p>
                        </div>
                      ))}
                      {dayJobs.length > 3 && <p className="text-xs text-slate-400 text-center">+{dayJobs.length - 3} más</p>}
                      <div 
                        onClick={() => openNewJob(day)}
                        className="w-full text-xs text-center py-1 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <Plus className="w-3 h-3 inline mr-1" />Añadir
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Today's Jobs Detail */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Trabajos de hoy</CardTitle></CardHeader>
          <CardContent>
            {getJobsForDay(new Date()).length === 0 ? (
              <p className="text-slate-500 text-center py-4">No hay trabajos programados para hoy</p>
            ) : (
              <div className="space-y-3">
                {getJobsForDay(new Date()).map(job => (
                  <div
                    key={job.id}
                    onClick={() => openJobDetail(job)}
                    className={cn(
                      "w-full p-4 rounded-lg flex items-center justify-between cursor-pointer transition-all border-2 border-transparent hover:border-sky-400",
                      job.is_emergency ? "bg-red-50" : "bg-slate-50 hover:bg-slate-100"
                    )}
                    role="button"
                    tabIndex={0}
                    data-testid={`today-job-${job.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{job.title}</h4>
                        {job.is_emergency && <Badge className="bg-red-500 text-white">URGENTE</Badge>}
                        {isRecurring(job) && <Badge variant="outline" className="text-xs">Recurrente</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{job.client_name}</span>
                        {job.worker_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{job.worker_name}</span>}
                        {job.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{job.scheduled_time}</span>}
                      </div>
                    </div>
                    <Badge className={cn(job.status === 'completed' ? 'bg-green-100 text-green-800' : job.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800')}>
                      {getStatusLabel(job.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Detail Dialog */}
        <Dialog open={jobDetailOpen} onOpenChange={setJobDetailOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedJob?.title}
                {selectedJob?.is_emergency && <Badge className="bg-red-500 text-white">URGENTE</Badge>}
              </DialogTitle>
            </DialogHeader>
            {selectedJob && (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <div>
                      <p className="font-medium">{selectedJob.client_name}</p>
                      {selectedJob.client_address && <p className="text-sm text-slate-500">{selectedJob.client_address}</p>}
                    </div>
                  </div>
                  {selectedJob.worker_name && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <User className="w-4 h-4" />
                      <span>{selectedJob.worker_name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>{format(parseISO(selectedJob.scheduled_date), "PPP", { locale: es })} {selectedJob.scheduled_time ? `a las ${selectedJob.scheduled_time}` : ''}</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge className={cn(
                    selectedJob.status === 'completed' ? 'bg-green-100 text-green-800' : 
                    selectedJob.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-blue-100 text-blue-800'
                  )}>
                    {getStatusLabel(selectedJob.status)}
                  </Badge>
                  {isRecurring(selectedJob) && <Badge variant="outline">Recurrente</Badge>}
                </div>

                {selectedJob.description && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">{selectedJob.description}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <Button onClick={() => handleEdit(selectedJob)} className="flex-1" variant="default">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                  <Button onClick={() => handleDeleteClick(selectedJob)} className="flex-1" variant="destructive">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar trabajo?</AlertDialogTitle>
              <AlertDialogDescription>
                {jobToDelete && isRecurring(jobToDelete) 
                  ? 'Este es un trabajo recurrente. ¿Quieres eliminar solo este trabajo o toda la serie?' 
                  : 'Esta acción no se puede deshacer. ¿Estás seguro?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              {jobToDelete && isRecurring(jobToDelete) ? (
                <>
                  <Button variant="outline" onClick={() => handleDelete(false)}>Solo este</Button>
                  <AlertDialogAction onClick={() => handleDelete(true)} className="bg-red-600 hover:bg-red-700">
                    Toda la serie
                  </AlertDialogAction>
                </>
              ) : (
                <AlertDialogAction onClick={() => handleDelete(false)} className="bg-red-600 hover:bg-red-700">
                  Eliminar
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </OwnerLayout>
  );
};

export default CalendarPage;
